/**
 * Generic wizard engine. An action module supplies `steps`, `summary(a, court)` and
 * `build(a, court)`; this file renders the questions, the review step and the checklist.
 *
 * `court` is the court context from lib/courtlayer.js (its courts.json entry, court-layer
 * details and calendar). A module's `steps` may be an array or a function of the court, and
 * a step's `skipIf`, `intro`, and a field's `show`, `opts`, `more`, `label`, `hint`, `none` and
 * `searchLabel` may be functions of (answers, court). An option of a check field is { v, l, h: hint, tag,
 * on: checked when first offered }.
 */
import { Button } from "exo-ds/button/vanilla";
import { Checkbox } from "exo-ds/checkbox/vanilla";
import { Field } from "exo-ds/field/vanilla";
import { Radio } from "exo-ds/radio/vanilla";
import { Select } from "exo-ds/select/vanilla";
import { TextInput } from "exo-ds/text-input/vanilla";
import { h } from "./dom.js";
import { todayISO } from "./lib/courtdays.js";
import { courtContext } from "./lib/courtlayer.js";
import { citeText } from "./rules.js";

const storeKey = (courtId, actionId) => `opencourt:v2:${courtId}:${actionId}`;

// An action whose checklist items were renamed (`renamedChecks`: old key -> the keys that took its place) moves
// the ticks saved under an old key to the new ones.
function loadState(key, action) {
  try {
    const s = JSON.parse(localStorage.getItem(key) || "null");
    if (s && s.a) {
      Object.entries((action && action.renamedChecks) || {}).forEach(([old, keys]) => {
        if (!(old in (s.checks || {}))) return;
        keys.forEach((k) => { if (!(k in s.checks)) s.checks[k] = s.checks[old]; });
        delete s.checks[old];
      });
      return s;
    }
  } catch (e) { /* storage unavailable */ }
  return { a: {}, step: 0, done: false, checks: {} };
}
function saveState(key, state) {
  try { localStorage.setItem(key, JSON.stringify(state)); } catch (e) { /* storage unavailable */ }
}

/**
 * `court` is the court's row in src/content/courts.json. `formLabel(id)` names a forms.json id
 * for the links a checklist item makes to its forms, and `formHref(id)` is where they go.
 * Answers are saved under `key` (by default the court's and the action's; a case passes its own,
 * so each case keeps its own answers) and `onDone(answers)` runs when the checklist is generated;
 * when it returns false the checklist isn't shown (it has gone somewhere else). `cancel`
 * ({ label, href, onclick }) takes the place of Back on the first step. `prefill` (answers, as { key: value })
 * fills the questions not answered yet until the checklist is generated, each marked as filled in from the case,
 * and `onAnswers(answers)` runs each time the answers are saved: a case passes both, so what one action's wizard
 * is told about the case is filled in in another's.
 */
export function mountWizard(container, action, court, actionName, {
  formLabel = (id) => id, formHref, key, onDone, cancel, prefill, onAnswers,
} = {}) {
  const ctx = courtContext(court);
  const href = formHref || ((id) => `#/${ctx.id}/forms/${encodeURIComponent(id)}`);
  const formLink = (id) => h("a", { class: "form-link", href: href(id) }, formLabel(id));
  key = key || storeKey(ctx.id, action.id);
  let state = loadState(key, action);
  const persist = () => { saveState(key, state); if (onAnswers) onAnswers(state.a); };
  // The answers filled in from `prefill`, marked as such while they keep that value.
  const filled = new Set();
  const fill = () => {
    if (!prefill || state.done) return;
    Object.entries(prefill).forEach(([k, v]) => {
      if (v !== undefined && state.a[k] === undefined) { state.a[k] = v; filled.add(k); }
    });
  };
  fill();

  // A value, or a function of (answers, court).
  const val = (x) => (typeof x === "function" ? x(state.a, ctx) : x);
  // Text with its citations as buttons that open the rules they cite (src/rules.js).
  const cited = (x) => citeText(x, ctx.id);
  const actionSteps = typeof action.steps === "function" ? action.steps(ctx) : action.steps;
  const reviewStep = { id: "review", title: "Review your answers", intro: "Check the summary, then generate your checklist.", review: true };
  const visibleSteps = () => [...actionSteps, reviewStep].filter((s) => !(s.skipIf && s.skipIf(state.a, ctx)));
  const fieldVisible = (f) => !f.show || f.show(state.a, ctx);
  const optsOf = (f) => val(f.opts);
  const stepValid = (s) => s.review || s.fields.every((f) => !fieldVisible(f) || !f.req || (state.a[f.k] !== undefined && state.a[f.k] !== ""));

  function scrollTop() {
    container.scrollTop = 0;
    const pane = container.closest(".pane");
    if (pane) pane.scrollTop = 0;
  }

  /* ---------------------------------------------------------------- steps */
  // A radio or checkbox card: its label text (nodes too), and the hint under it with its citations as buttons.
  const choice = (Kind, label, hint, props) => {
    const el = Kind({ variant: "card", ...props, description: hint ? "" : undefined, children: [label].flat().filter((x) => x != null) });
    if (hint) el.querySelector(".exo-choice__description").replaceChildren(...cited(hint));
    return el;
  };

  function renderField(f) {
    const cur = state.a[f.k];
    const id = `field-${f.k}`;
    const kids = [];
    const group = f.type === "radio" || f.type === "check";
    if (f.type === "radio") {
      optsOf(f).forEach((o) => kids.push(choice(Radio, o.l, o.h, { name: f.k, value: o.v, defaultChecked: cur === o.v, required: !!f.req,
        onChange: () => { state.a[f.k] = o.v; persist(); renderStep(); } })));
    } else if (f.type === "check") {
      // With `none` (its label), the field ends with a "none of these" choice that clears the options listed and
      // searches `more`, further options ({ v, l, s: extra text to match }); the ones picked from it are listed too.
      // An option with `on` is checked the first time it is offered (`<k>Seen` keeps the ones offered), so one the
      // user unchecks stays unchecked, and one a changed answer brings in is checked.
      const set = new Set(cur || []);
      const noneKey = `${f.k}None`, seenKey = `${f.k}Seen`;
      const save = () => { state.a[f.k] = [...set]; persist(); renderStep(); };
      const seen = new Set(state.a[seenKey] || []);
      const fresh = optsOf(f).filter((o) => !seen.has(o.v));
      if (fresh.length) {
        fresh.forEach((o) => { seen.add(o.v); if (o.on) set.add(o.v); });
        state.a[seenKey] = [...seen];
        state.a[f.k] = [...set];
        persist();
      }
      const opt = (o, onChange) => kids.push(choice(Checkbox, [o.l, o.tag ? h("span", { class: "tag" }, o.tag) : null], o.h,
        { name: f.k, value: o.v, defaultChecked: set.has(o.v), onChange }));
      const listed = optsOf(f);
      listed.forEach((o) => opt(o, (e) => {
        if (e.target.checked) { set.add(o.v); delete state.a[noneKey]; } else set.delete(o.v);
        save();
      }));
      if (f.none) {
        const more = (val(f.more) || []).filter((o) => !listed.some((x) => x.v === o.v));
        more.filter((o) => set.has(o.v)).forEach((o) => opt(o, () => { set.delete(o.v); save(); }));
        const none = !!state.a[noneKey];
        kids.push(choice(Checkbox, val(f.none), null, { name: noneKey, defaultChecked: none, onChange: (e) => {
          if (e.target.checked) { state.a[noneKey] = true; listed.forEach((o) => set.delete(o.v)); } else delete state.a[noneKey];
          save();
          const q = container.querySelector(".field .menu-filter");
          if (q) q.focus();
        } }));
        if (none) {
          const results = h("div", { class: "picker-results" });
          const input = TextInput({ type: "search", size: "sm", width: "full", class: "menu-filter", placeholder: val(f.searchLabel) || "Search", "aria-label": val(f.searchLabel) || "Search",
            onInput: () => {
              const q = input.value.trim().toLowerCase();
              if (!q) { results.replaceChildren(); return; }
              const hits = more.filter((o) => !set.has(o.v) && `${o.l} ${o.s || ""}`.toLowerCase().includes(q)).slice(0, 8);
              results.replaceChildren(...(hits.length
                ? hits.map((o) => h("button", { type: "button", class: "menu-item", onclick: () => {
                  set.add(o.v); save();
                  const next = container.querySelector(".field .menu-filter");
                  if (next) next.focus();
                } }, o.l))
                : [h("div", { class: "menu-note" }, "No matches.")]));
            },
            onKeydown: (e) => { if (e.key === "Enter") { e.preventDefault(); const first = results.querySelector("button"); if (first) first.click(); } } });
          kids.push(h("div", { class: "picker" }, input, results));
        }
      }
    } else if (f.type === "text") {
      // Enter moves on, like Next.
      kids.push(TextInput({ width: "full", class: "wide", name: f.k, defaultValue: cur || "", maxlength: f.max ? String(f.max) : null, autocomplete: "off",
        onInput: (e) => { state.a[f.k] = e.target.value.trim() ? e.target.value : ""; persist(); const nx = container.querySelector("#next"); if (nx) nx.disabled = !stepValid(currentStep()); },
        onKeydown: (e) => { if (e.key === "Enter") { e.preventDefault(); const nx = container.querySelector("#next"); if (nx && !nx.disabled) nx.click(); } } }));
    } else if (f.type === "select") {
      // Options with a `group` are listed under it.
      if (cur === undefined && f.def) { state.a[f.k] = val(f.def); persist(); }
      kids.push(Select({ width: "full", class: "wide", name: f.k, defaultValue: state.a[f.k],
        options: optsOf(f).map((o) => ({ value: o.v, label: o.l, group: o.group || undefined })),
        onChange: (e) => { state.a[f.k] = e.target.value; persist(); renderStep(); } }));
    } else if (f.type === "date") {
      if (cur === undefined && f.def) state.a[f.k] = f.def();
      kids.push(TextInput({ type: "date", name: f.k, defaultValue: state.a[f.k] || "",
        onInput: (e) => { state.a[f.k] = e.target.value; persist(); const nx = container.querySelector("#next"); if (nx) nx.disabled = !stepValid(currentStep()); } }));
    }
    // The hint, and a note when the answer was filled in from the case, both go in Field's hint, which the control is
    // described by; the hint's citations are buttons.
    const hint = val(f.hint);
    const prefilled = filled.has(f.k) && cur === prefill[f.k];
    const box = Field({ id, class: "field", label: val(f.label), required: !!f.req, group, hint: hint || prefilled ? "" : undefined, children: kids });
    if (hint || prefilled) {
      box.querySelector(".exo-field__hint").replaceChildren(...cited(hint),
        prefilled ? h("span", { class: "filled" }, "Filled in from your answers for another action in this case.") : "");
    }
    return box;
  }

  const currentStep = () => {
    const steps = visibleSteps();
    return steps[Math.min(state.step, steps.length - 1)];
  };

  function renderStep() {
    const steps = visibleSteps();
    const s = currentStep();
    const idx = steps.indexOf(s);
    const last = idx === steps.length - 1;
    container.replaceChildren(
      h("div", { class: "wiz" },
        h("div", { class: "wiz-head" },
          h("div", { class: "eyebrow" }, actionName),
          h("div", { class: "progress", role: "progressbar", "aria-valuemin": 1, "aria-valuemax": steps.length, "aria-valuenow": idx + 1 },
            h("i", { style: `width:${Math.round(((idx + 1) / steps.length) * 100)}%` })),
          h("div", { class: "stepno" }, `Step ${idx + 1} of ${steps.length}`)),
        // The step itself, apart from its head and its buttons, so a page can scroll it on its own.
        h("div", { class: "wiz-body" },
          h("h2", { tabindex: "-1", id: "step-title" }, s.title),
          h("p", { class: "lead" }, cited(val(s.intro) || "")),
          s.review
            ? h("dl", { class: "summary" }, action.summary(state.a, ctx).flatMap(([k, v]) => [h("dt", null, k), h("dd", null, v)]))
            : s.fields.filter(fieldVisible).map(renderField),
          h("p", { class: "disclaimer" }, "General information from the downloaded rules and statutes. It is not legal advice.")),
        h("div", { class: "nav" },
          idx === 0 && cancel
            ? Button({ href: cancel.href, onClick: cancel.onclick, children: cancel.label || "Cancel" })
            : Button({ disabled: idx === 0, onClick: () => { state.step = Math.max(0, idx - 1); persist(); renderStep(); scrollTop(); }, children: "Back" }),
          Button({ variant: "primary", id: "next", disabled: !stepValid(s),
            onClick: () => {
              if (last) { state.done = true; persist(); if (onDone && onDone(state.a) === false) return; renderResult(); }
              else { state.step = idx + 1; persist(); renderStep(); }
              scrollTop();
            }, children: last ? "Generate checklist" : "Next" }))));
    const text = container.querySelector('.field input[type="text"]');
    if (text) text.focus();
  }

  /* --------------------------------------------------------------- result */
  function checklistText(m) {
    let t = `${actionName.toUpperCase()} - CHECKLIST\n\n`;
    m.rows.forEach(([k, v]) => { t += `${k}: ${v}\n`; });
    t += "\n";
    m.flags.forEach((f) => { t += `! ${[f.b, f.m].filter(Boolean).join(" ")}\n`; });
    m.sections.forEach((s) => {
      t += `\n${s.title.toUpperCase()}\n`;
      s.items.forEach((i) => {
        t += `${state.checks[i.key] ? "[x]" : "[ ]"} ${i.text}${i.cite ? ` [${i.cite}]` : ""}\n`;
        if (i.note) t += `    ${i.note}\n`;
        if (i.forms && i.forms.length) t += `    Forms: ${i.forms.map(formLabel).join(", ")}\n`;
      });
    });
    t += `\nTO VERIFY\n${m.verify.map((v) => `- ${v}`).join("\n")}\n`;
    return t;
  }

  function renderResult() {
    const m = action.build(state.a, ctx);
    const copyBtn = Button({ onClick: async () => {
      const text = checklistText(m);
      try { await navigator.clipboard.writeText(text); copyBtn.textContent = "Copied"; }
      catch (e) { copyBtn.textContent = "Copy failed"; }
      setTimeout(() => { copyBtn.textContent = "Copy as text"; }, 1800);
    }, children: "Copy as text" });
    container.replaceChildren(
      h("div", { class: "wiz result" },
        h("div", { class: "eyebrow" }, actionName),
        h("h2", { tabindex: "-1" }, m.title),
        h("div", { class: "tools" },
          Button({ onClick: () => window.print(), children: "Print" }),
          copyBtn,
          Button({ onClick: () => { state.done = false; state.step = 0; persist(); renderStep(); scrollTop(); }, children: "Edit answers" }),
          Button({ onClick: () => { if (confirm("Clear all answers and start over?")) { state = { a: {}, step: 0, done: false, checks: {} }; filled.clear(); fill(); persist(); renderStep(); scrollTop(); } }, children: "Start over" })),
        m.flags.map((f) => h("div", { class: `flag ${f.t}` }, f.b ? h("b", null, f.b + " ") : null, cited(f.m))),
        h("table", { class: "facts" }, h("tbody", null, m.rows.map(([k, v, sub]) =>
          h("tr", null, h("th", null, k), h("td", null, v, sub ? h("div", { class: "cite" }, cited(sub)) : null))))),
        m.sections.map((s) =>
          h("section", { class: "phase" },
            h("h3", null, s.title),
            s.blurb ? h("p", { class: "blurb" }, cited(s.blurb)) : null,
            s.items.map((i) => {
              const on = !!state.checks[i.key];
              const row = Checkbox({ class: "ck" + (on ? " done" : ""), defaultChecked: on,
                onChange: (e) => { state.checks[i.key] = e.target.checked; persist(); row.classList.toggle("done", e.target.checked); },
                children: [
                  h("span", { class: "m" }, cited(i.text)),
                  i.cite ? h("span", { class: "cite" }, " [", cited(i.cite), "]") : null,
                  i.note ? h("span", { class: "note" }, cited(i.note)) : null,
                  i.forms && i.forms.length ? h("span", { class: "forms" }, "Forms: ", i.forms.flatMap((id, n) => (n ? [", ", formLink(id)] : [formLink(id)]))) : null,
                ].filter(Boolean) });
              return row;
            }))),
        h("section", { class: "phase" },
          h("h3", null, "Not covered by the downloaded files (verify)"),
          h("ul", { class: "verify" }, m.verify.map((v) => h("li", null, v)))),
        h("p", { class: "disclaimer" }, "General information from the downloaded rules and statutes. It is not legal advice. Confirm current requirements with the court clerk before filing.")));
  }

  if (state.done) renderResult(); else renderStep();
}

/**
 * How far along an action's checklist saved under `key` is: null before its checklist is generated,
 * else { done, total } items ticked. `court` is the court's row in courts.json.
 */
export function checklistProgress(key, action, court) {
  const state = loadState(key, action);
  if (!state.done) return null;
  const items = action.build(state.a, courtContext(court)).sections.flatMap((s) => s.items);
  return { done: items.filter((i) => state.checks[i.key]).length, total: items.length };
}

/** Whether a checklist has been generated from the answers saved under `key`. */
export const isDone = (key) => loadState(key).done;

export { todayISO };
